import React, { useState, useEffect, useRef } from 'react';
import { db } from '../../firebaseConfig';
import { collection, query, where, getDocs, doc, getDoc, setDoc } from 'firebase/firestore';
import { PERIODOS, VALORACIONES } from '../../constants';

// --- DATOS FIJOS DE LA INSTITUCIÓN ---
const schoolDetails = {
    name: "INSTITUCIÓN EDUCATIVA SAN JOSÉ",
    address: "Corregimiento Puerto Ánchica, Municipio de Montelibano, Córdoba",
    resolution: "Resolución 362 de julio 4 de 2011 y 003754 de nov. 30 de 2017",
    dane: "223466001537",
    nit: "812004980-7",
    contact: "Cel: 3113701655 - Email: ee_22346600153701@sedcordoba.gov.co"
};

// --- Sub-componente para la Vista Previa del Boletín ---
function ReportPreview({ bulletinData, teacherName }) {
    const { student, period, dimensions, observations } = bulletinData;

    const studentInfo = {
        grado: student.grado,
        anioLectivo: new Date().getFullYear(),
        periodo: period,
        fechaExpedicion: new Date().toLocaleDateString('es-CO'),
        apellidosNombres: student.nombreCompleto,
        docIdentidad: student.documentoIdentidad,
        codigoMatricula: student.id.slice(0, 8).toUpperCase(), // Simulado
        grupo: '1', // Simulado
        sede: 'Principal (Puerto Ánchica)', // Simulado
        inasistencias: 0 // Simulado
    };

    const signaturePositions = {
        rector: "Rector(a)",
        groupDirector: "Director de Grupo" // Nuevo título
    };

    return (
        <div className="bg-white text-black font-sans leading-normal tracking-normal printable-area shadow-lg p-8 rounded-xl max-w-[21cm] mx-auto">
            {/* --- Encabezado --- */}
            <header className="border-b-2 border-slate-800 pb-4 mb-6 flex items-center justify-between gap-6">
                <img
                    src="/images/escudo-san-jose.jpg"
                    alt="Escudo de la Institución"
                    className="h-28 w-28 object-contain"
                    onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/100x100/EEE/31343C?text=Logo'; }}
                />
                <div className="text-center flex-grow space-y-1">
                    <h1 className="text-xl font-bold uppercase tracking-wide">{schoolDetails.name}</h1>
                    <p className="text-sm">{schoolDetails.address}</p>
                    <p className="text-sm">Resolución: {schoolDetails.resolution}</p>
                    <p className="text-sm font-semibold">DANE: {schoolDetails.dane} - NIT: {schoolDetails.nit}</p>
                    <p className="text-sm">{schoolDetails.contact}</p>
                    <h2 className="text-lg font-bold mt-4 uppercase border-t border-slate-300 pt-2">Informe Valorativo de Desempeño</h2>
                </div>
            </header>

            {/* --- Info Estudiante --- */}
            <section className="mb-6 border border-slate-800 rounded-lg overflow-hidden">
                <div className="bg-slate-100 p-2 border-b border-slate-800 font-bold text-sm uppercase text-center">Información del Estudiante</div>
                <div className="p-4 grid grid-cols-2 gap-4 text-sm">
                    <p><span className="font-bold">Apellidos y Nombres:</span> {studentInfo.apellidosNombres}</p>
                    <p><span className="font-bold">Documento de Identidad:</span> {studentInfo.docIdentidad}</p>
                    <p><span className="font-bold">Grado:</span> {studentInfo.grado}</p>
                    <p><span className="font-bold">Año Lectivo - Periodo:</span> {studentInfo.anioLectivo} - {studentInfo.periodo}</p>
                    <p><span className="font-bold">Fecha Expedición:</span> {studentInfo.fechaExpedicion}</p>
                </div>
            </section>

            {/* --- Escala Valorativa --- */}
            <section className="mb-6 text-center text-xs">
                <h4 className="font-bold mb-2 uppercase text-slate-600">Escala de Valoración Institucional</h4>
                <div className="flex justify-center flex-wrap gap-4 border-t border-b border-slate-200 py-2">
                    {Object.entries(VALORACIONES).map(([key, value]) => (
                        <span key={key} className="bg-slate-50 px-2 py-1 rounded border border-slate-100"><strong className="text-slate-900">{key}:</strong> {value}</span>
                    ))}
                </div>
            </section>

            {/* --- Dimensiones y Logros --- */}
            <section className="space-y-6">
                {Object.entries(dimensions).map(([dimension, logros]) => (
                    <div key={dimension} className="break-inside-avoid">
                        <h3 className="bg-slate-800 text-white p-2 text-sm font-bold uppercase rounded-t-lg text-center tracking-wider">{dimension}</h3>
                        <table className="w-full border-collapse border-x border-b border-slate-300 text-sm">
                            <thead>
                                <tr className="bg-slate-100 text-xs">
                                    <th className="border-b border-slate-300 p-2 text-left font-bold w-3/4">Logros / Aspectos a Evaluar</th>
                                    <th className="border-b border-slate-300 p-2 text-center font-bold">Valoración</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200">
                                {logros.map((logro, index) => (
                                    <tr key={index} className="even:bg-slate-50">
                                        <td className="p-2 border-r border-slate-200">{logro.descripcion}</td>
                                        <td className="p-2 text-center font-bold text-slate-800">{logro.valoracion}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ))}
            </section>

            {/* --- Observaciones --- */}
            <section className="mt-6 break-inside-avoid">
                <h3 className="text-sm font-bold uppercase mb-2">Observaciones Generales / Acompañamiento Intencionado</h3>
                <div className="border border-slate-300 rounded p-4 min-h-[4rem] text-sm italic bg-slate-50">
                    {observations || "Sin observaciones registradas."}
                </div>
            </section>

            {/* --- Firmas --- */}
            <section className="mt-12 break-inside-avoid">
                <p className="text-sm mb-12"><strong>Elaborado por Docente:</strong> {teacherName}</p>
                <div className="flex justify-around items-end pt-8">
                    <div className="text-center w-64">
                        <div className="border-t border-slate-800 pt-2 font-bold mb-1">Rector(a)</div>
                        <p className="text-xs text-slate-500">{signaturePositions.rector}</p>
                    </div>
                    <div className="text-center w-64">
                        <div className="border-t border-slate-800 pt-2 font-bold mb-1">Director(a) de Grupo</div>
                        <p className="text-xs text-slate-500">{signaturePositions.groupDirector}</p>
                    </div>
                </div>
                <p className="text-center mt-12 text-xs text-slate-500">Expedido en el municipio de Montelíbano, el día {new Date().toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' })}.</p>
            </section>
        </div>
    );
}


// --- Componente Principal ---
export default function BulletinGenerator({ user, currentYear }) {
    const [students, setStudents] = useState([]);
    const [selectedStudentId, setSelectedStudentId] = useState('');
    const [selectedPeriodo, setSelectedPeriodo] = useState(PERIODOS[0]);
    const [observations, setObservations] = useState('');
    const [bulletinData, setBulletinData] = useState(null);
    const [loading, setLoading] = useState(false);
    const bulletinRef = useRef();

    useEffect(() => {
        const fetchStudents = async () => {
            if (!user || !currentYear) return;
            // Fetch all, filter in memory for year/legacy
            const q = query(collection(db, "estudiantes"), where("profesorId", "==", user.uid));
            const querySnapshot = await getDocs(q);
            const list = querySnapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(s => (s.year || 2025) === currentYear);
            setStudents(list);
        };
        fetchStudents();
    }, [user, currentYear]);

    // Carga las observaciones guardadas cuando se selecciona un estudiante o período
    useEffect(() => {
        const fetchObservations = async () => {
            if (!selectedStudentId) return;
            const obsDocRef = doc(db, "observaciones", `${selectedStudentId}_${selectedPeriodo}`);
            const docSnap = await getDoc(obsDocRef);
            if (docSnap.exists()) {
                setObservations(docSnap.data().texto);
            } else {
                setObservations('');
            }
        };
        fetchObservations();
    }, [selectedStudentId, selectedPeriodo]);

    const generateBulletin = async () => {
        if (!selectedStudentId) {
            alert("Por favor, seleccione un estudiante.");
            return;
        }
        setLoading(true);
        setBulletinData(null);

        // Guardar observaciones antes de generar
        const obsDocRef = doc(db, "observaciones", `${selectedStudentId}_${selectedPeriodo}`);
        await setDoc(obsDocRef, { texto: observations, estudianteId: selectedStudentId, periodo: selectedPeriodo, profesorId: user.uid });

        try {
            const student = students.find(s => s.id === selectedStudentId);
            const gradeDocRef = doc(db, "calificaciones", `${selectedStudentId}_${selectedPeriodo}`);
            const gradeSnap = await getDoc(gradeDocRef);
            const studentGrades = gradeSnap.exists() ? gradeSnap.data().calificaciones : {};
            const achievementQuery = query(collection(db, "logros"), where("profesorId", "==", user.uid), where("grado", "==", student.grado), where("periodo", "==", Number(selectedPeriodo)));
            const achievementSnap = await getDocs(achievementQuery);
            const achievements = achievementSnap.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(ach => (ach.year || 2025) === currentYear); // Filter ach by year

            const dataByDimension = {};
            achievements.forEach(ach => {
                if (!dataByDimension[ach.dimension]) dataByDimension[ach.dimension] = [];
                dataByDimension[ach.dimension].push({
                    descripcion: ach.descripcion,
                    valoracion: studentGrades[ach.id] || 'N/C'
                });
            });

            setBulletinData({ student, period: selectedPeriodo, dimensions: dataByDimension, observations });
        } catch (error) {
            alert("Error al generar el boletín: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = () => {
        const printContents = bulletinRef.current.innerHTML;
        const originalContents = document.body.innerHTML;
        // Styles for printing to ensure it looks exactly like the preview
        const styles = `
            <style> 
                @media print {
                    body { 
                        -webkit-print-color-adjust: exact !important; 
                        print-color-adjust: exact !important; 
                        background-color: white !important;
                        margin: 0;
                        padding: 0;
                    } 
                    .printable-area { 
                        box-shadow: none !important; 
                        max-width: 100% !important;
                        margin: 0 !important;
                        padding: 2cm !important;
                    }
                    .no-print { display: none !important; }
                }
            </style>`;
        document.body.innerHTML = styles + printContents;
        window.print();
        document.body.innerHTML = originalContents;
        window.location.reload(); // Reload to restore state and events completely
    };

    return (
        <div className="space-y-8 animate-fade-in">
            {/* Header / Selector Section */}
            <div className="glass-card p-8 rounded-2xl">
                <h2 className="text-2xl font-bold mb-6 text-slate-800 bg-clip-text text-transparent bg-gradient-to-r from-primary-600 to-secondary-600">
                    Generar Boletín Informativo
                </h2>

                <div className="grid lg:grid-cols-4 gap-6 items-end">
                    <div className="lg:col-span-1">
                        <label className="block text-slate-600 font-semibold mb-2">Período</label>
                        <select value={selectedPeriodo} onChange={(e) => setSelectedPeriodo(e.target.value)} className="glass-input w-full px-4 py-3 rounded-xl">
                            {PERIODOS.map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                    </div>

                    <div className="lg:col-span-2">
                        <label className="block text-slate-600 font-semibold mb-2">Estudiante</label>
                        <select value={selectedStudentId} onChange={(e) => setSelectedStudentId(e.target.value)} className="glass-input w-full px-4 py-3 rounded-xl">
                            <option value="">Seleccione un estudiante</option>
                            {students.map(s => <option key={s.id} value={s.id}>{s.nombreCompleto}</option>)}
                        </select>
                    </div>

                    <div className="lg:col-span-1">
                        <button
                            onClick={generateBulletin}
                            disabled={loading || !selectedStudentId}
                            className="btn-primary w-full py-3 rounded-xl font-bold disabled:opacity-70 disabled:cursor-not-allowed flex justify-center items-center"
                        >
                            {loading ? (
                                <span className="flex items-center gap-2">
                                    <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                    Generando...
                                </span>
                            ) : (
                                'Generar Boletín'
                            )}
                        </button>
                    </div>
                </div>

                <div className="mt-6">
                    <label className="block text-slate-600 font-semibold mb-2">Observaciones Generales</label>
                    <textarea
                        value={observations}
                        onChange={(e) => setObservations(e.target.value)}
                        rows="3"
                        className="glass-input w-full px-4 py-3 rounded-xl"
                        placeholder="Escriba aquí las observaciones cualitativas, recomendaciones o plan de mejoramiento para el estudiante..."
                    />
                </div>
            </div>

            {/* Preview Section */}
            {bulletinData && (
                <div className="animate-slide-up">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-xl font-bold text-slate-700">Vista Previa</h3>
                        <button onClick={handlePrint} className="btn-secondary px-6 py-2 rounded-xl flex items-center gap-2 shadow-lg">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"></path></svg>
                            Imprimir Boletín
                        </button>
                    </div>

                    <div className="overflow-x-auto pb-8">
                        {/* Wrapper for the print preview to simulate paper on screen */}
                        <div className="min-w-max md:min-w-0 flex justify-center">
                            <div ref={bulletinRef}>
                                <ReportPreview bulletinData={bulletinData} teacherName={user.displayName || user.email} />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
